import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { AuthProvider } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import LoginScreen from "./screens/LoginScreen";
import DashboardScreen from "./screens/DashboardScreen";
import BarcodeScannerScreen from "./screens/BarcodeScannerScreen";
import CameraScreen from "./screens/CameraScreen";
import DarkSpotScreen from "./screens/DarkSpotScreen";
import MMSBlisterScreen from "./screens/MMSBlisterScreen";
import TTDCapsuleScreen from "./screens/TTDCapsuleScreen";
import ResultsScreen from "./screens/ResultsScreen";
import HistoryScreen from "./screens/HistoryScreen";
import PrivateRoute from "./components/PrivateRoute";
import BottomNavigation from "./components/BottomNavigation";
import "./index.css";

// Pages with no in-progress scan, where reloading to a new deploy loses nothing.
const SAFE_RELOAD_PATHS = ["/", "/login"];
let updateReady = false;

function UpdateReloader() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (updateReady && SAFE_RELOAD_PATHS.includes(pathname)) window.location.reload();
  }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    // Register service worker for PWA functionality
    if ("serviceWorker" in navigator) {
      // A page without a controller is a first install, not an upgrade.
      const hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (!hadController) return;
        updateReady = true;
        if (SAFE_RELOAD_PATHS.includes(window.location.pathname)) window.location.reload();
      });

      window.addEventListener("load", () => {
        navigator.serviceWorker
          .register("/sw.js", { updateViaCache: "none" })
          .then((registration) => {
            console.log("SW registered: ", registration);
            // Installed PWAs stay open for days; check for a new deploy whenever the app is reopened.
            document.addEventListener("visibilitychange", () => {
              if (document.visibilityState === "visible") registration.update();
            });
          })
          .catch((registrationError) => {
            console.log("SW registration failed: ", registrationError);
          });
      });
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <UpdateReloader />
          <div data-testid="app-container" className="App min-h-screen bg-gray-50 dark:bg-gray-900 pb-24 md:pb-0">
            <Routes>
              <Route path="/login" element={<LoginScreen />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <DashboardScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/scanner"
                element={
                  <PrivateRoute>
                    <BarcodeScannerScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/camera"
                element={
                  <PrivateRoute>
                    <CameraScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dark-spot"
                element={
                  <PrivateRoute>
                    <DarkSpotScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/mms-blister-detection"
                element={
                  <PrivateRoute>
                    <MMSBlisterScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ttd-detection"
                element={
                  <PrivateRoute>
                    <TTDCapsuleScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/results"
                element={
                  <PrivateRoute>
                    <ResultsScreen />
                  </PrivateRoute>
                }
              />
              <Route
                path="/history"
                element={
                  <PrivateRoute>
                    <HistoryScreen />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <BottomNavigation />
            <Toaster
              position="top-center"
              toastOptions={{
                duration: 4000,
                style: {
                  background: "#363636",
                  color: "#fff",
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
